// UPDATE 1: In PIIProtectionTab component - Replace the entire component

import React, { useState, useEffect } from "react";
import {
  Lock,
  Unlock,
  Eye,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Download,
  ChevronDown,
  Database,
  Search,
  Shield,
  Zap,
  FileText,
} from "lucide-react";

const PIIProtectionTab = ({ piiDetectionData, sessionId, fileName }) => {
  const [stage, setStage] = useState("initial"); // initial, encrypting, encrypted, decrypting, decrypted
  const [encryptedTables, setEncryptedTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [encryptedPreview, setEncryptedPreview] = useState(null);
  const [decryptedPreview, setDecryptedPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTableDropdownOpen, setIsTableDropdownOpen] = useState(false);
  const [searchTableTerm, setSearchTableTerm] = useState("");
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [hasEncryptedData, setHasEncryptedData] = useState(false); // NEW: Track if data was saved

  // Fetch encrypted tables
  const fetchEncryptedTables = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        "https://apiowlsight.bluegecko.ai:8003/api/v1/cratedb/encrypted-tables"
      );
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      if (data.success && Array.isArray(data.tables)) {
        setEncryptedTables(data.tables);
      } else {
        setEncryptedTables([]);
      }
    } catch (err) {
      console.error("Error fetching encrypted tables:", err);
      setEncryptedTables([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch encrypted tables when encryption+save is complete
  useEffect(() => {
    if (hasEncryptedData && stage === "initial") {
      fetchEncryptedTables();
    }
  }, [hasEncryptedData, stage]);

  // Encryption API call
  const handleEncrypt = async () => {
    if (!sessionId || !fileName) {
      setError("Session ID or File Name missing from PII Detection");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const formData = new URLSearchParams();
      formData.append("session_id", sessionId);
      formData.append("file_name", fileName);

      const response = await fetch(
        "https://apiowlsight.bluegecko.ai:8003/api/v1/encrypt",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData.toString(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setEncryptedPreview(data);
        setStage("encrypted");
        setSuccessMessage("Data encrypted successfully!");
      } else {
        setError(data.message || "Encryption failed");
      }
    } catch (err) {
      console.error("Encryption error:", err);
      setError(err.message || "Failed to encrypt data");
    } finally {
      setIsLoading(false);
    }
  };

  // Save encrypted data to CrateDB
  const handleSaveEncrypted = async () => {
    if (!sessionId || !fileName) {
      setError("Session ID or File Name missing");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const formData = new URLSearchParams();
      formData.append("session_id", sessionId);
      formData.append("file_name", fileName);

      const response = await fetch(
        "https://apiowlsight.bluegecko.ai:8003/api/v1/save-to-cratedb",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData.toString(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(
          `✓ Encrypted data saved to CrateDB table: ${data.table_name}`
        );
        setHasEncryptedData(true); // NEW: Mark that encryption was saved
        setStage("saved"); // NEW: Set to saved state
        
        // Fetch updated encrypted tables list
        setTimeout(() => {
          fetchEncryptedTables();
        }, 500);
      } else {
        setError(data.message || "Failed to save encrypted data");
      }
    } catch (err) {
      console.error("Save error:", err);
      setError(err.message || "Failed to save encrypted data");
    } finally {
      setIsLoading(false);
    }
  };

  // Decryption API call - FIXED (remove schema prefix, add _encrypted suffix)
  const handleDecrypt = async () => {
    if (!selectedTable) {
      setError("Please select a table to decrypt");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Get the table name
      let tableName = selectedTable.full_name || selectedTable.table_name;
      
      // Remove schema prefix (e.g., "doc.table_name" -> "table_name")
      if (tableName.includes(".")) {
        tableName = tableName.split(".")[1];
      }
      
      // Add _encrypted suffix if not already present
      if (!tableName.endsWith("_encrypted")) {
        tableName = `${tableName}_encrypted`;
      }

      const formData = new URLSearchParams();
      formData.append("table_name", tableName);

      const response = await fetch(
        "https://apiowlsight.bluegecko.ai:8003/api/v1/decrypt",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData.toString(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setDecryptedPreview(data);
        setStage("decrypted");
        setSuccessMessage(
          `✓ Data decrypted successfully! Table restored: ${data.restored_table}`
        );
        setSelectedTable(null);
      } else {
        setError(data.message || "Decryption failed");
      }
    } catch (err) {
      console.error("Decryption error:", err);
      setError(err.message || "Failed to decrypt data. Please ensure the table exists and is properly encrypted.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetWorkflow = () => {
    setStage("initial");
    setEncryptedPreview(null);
    setDecryptedPreview(null);
    setSelectedTable(null);
    setError(null);
    setSuccessMessage(null);
    setSearchTableTerm("");
  };

  // Show initial state if no PII data
  if (!piiDetectionData || !piiDetectionData.pii_detection) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center bg-white p-16 rounded-3xl border border-gray-200 max-w-md w-full shadow-lg">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-purple-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            PII Protection Workflow
          </h3>
          <p className="text-gray-600 leading-relaxed mb-6">
            Complete PII Detection analysis first to enable encryption & protection
          </p>
          <p className="text-xs text-gray-500">
            ℹ️ Run analysis in the PII Detection tab to get started
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alert Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-900">Error</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-green-900">Success</p>
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Stage: Initial - Encryption Options ONLY */}
      {stage === "initial" && !hasEncryptedData && (
        <div className="space-y-6">
          {/* PII Detection Summary */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
            <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              PII Detection Summary
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-3 border border-blue-100">
                <p className="text-xs text-blue-600 font-semibold mb-1">
                  Total Columns
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {piiDetectionData.pii_detection?.total_columns || 0}
                </p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-blue-100">
                <p className="text-xs text-blue-600 font-semibold mb-1">
                  PII Columns
                </p>
                <p className="text-2xl font-bold text-red-600">
                  {piiDetectionData.pii_detection?.pii_column_count || 0}
                </p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-blue-100">
                <p className="text-xs text-blue-600 font-semibold mb-1">
                  Risk Level
                </p>
                <p
                  className="text-2xl font-bold"
                  style={{
                    color:
                      piiDetectionData.risk_assessment?.risk_level === "Critical"
                        ? "#dc3545"
                        : "#f59e0b",
                  }}
                >
                  {piiDetectionData.risk_assessment?.risk_level || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Encryption Action Card */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
                <Lock className="w-5 h-5 text-purple-600" />
                Encrypt & Protect PII Data
              </h3>
              <p className="text-sm text-gray-600">
                Step 1: Encrypt HIGH and MEDIUM risk PII columns for secure storage
              </p>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-xs font-semibold text-blue-900 mb-2">
                    📋 ENCRYPTION DETAILS
                  </p>
                  <p className="text-sm text-blue-800">
                    File: <span className="font-mono font-bold">{fileName}</span>
                  </p>
                  <p className="text-sm text-blue-800">
                    Session:{" "}
                    <span className="font-mono text-xs">
                      {sessionId?.slice(0, 12)}...
                    </span>
                  </p>
                  <p className="text-xs text-blue-700 mt-2">
                    ✓ Will encrypt {piiDetectionData.pii_detection?.pii_column_count || 0} PII columns
                  </p>
                </div>

                <button
                  onClick={handleEncrypt}
                  disabled={isLoading}
                  className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Encrypting...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      Step 1: Start Encryption
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stage: Encrypted - Show encrypted preview + Save button */}
      {stage === "encrypted" && encryptedPreview && (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-green-900 mb-2">✓ Encryption Complete</h3>
                <p className="text-sm text-green-800 mb-3">Data has been encrypted. Now save to CrateDB.</p>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="bg-white rounded p-3 border border-green-200">
                    <p className="text-xs text-green-700 font-semibold mb-1">
                      Columns Encrypted
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {encryptedPreview.columns_encrypted?.length || 0}
                    </p>
                  </div>
                  <div className="bg-white rounded p-3 border border-green-200">
                    <p className="text-xs text-green-700 font-semibold mb-1">
                      Rows Processed
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {encryptedPreview.rows_processed || 0}
                    </p>
                  </div>
                  <div className="bg-white rounded p-3 border border-green-200">
                    <p className="text-xs text-green-700 font-semibold mb-1">
                      Total Columns
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {encryptedPreview.total_columns || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Encrypted Preview */}
          {encryptedPreview.preview?.data && (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Encrypted Data Preview (First Row)
                </h3>
              </div>
              <div className="overflow-x-auto max-h-80 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      {Object.keys(encryptedPreview.preview.data[0] || {}).map(
                        (key) => (
                          <th
                            key={key}
                            className="px-4 py-2 text-left font-bold text-gray-700 whitespace-nowrap"
                          >
                            {key}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {encryptedPreview.preview.data.map((row, idx) => (
                      <tr key={idx} className="border-b border-gray-200">
                        {Object.values(row).map((value, i) => (
                          <td
                            key={i}
                            className="px-4 py-2 text-gray-600 font-mono text-[10px] break-all"
                          >
                            {String(value).slice(0, 50)}
                            {String(value).length > 50 ? "..." : ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-4 mb-2">
            <button
              onClick={handleSaveEncrypted}
              disabled={isLoading}
              className="px-6 py-3 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Step 2: Save to CrateDB
                </>
              )}
            </button>
            <button
              onClick={resetWorkflow}
              disabled={isLoading}
              className="px-6 py-3 rounded-lg bg-gray-200 text-gray-900 font-semibold hover:bg-gray-300 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Stage: Saved - Show Decryption Options ONLY */}
      {(stage === "initial" && hasEncryptedData || stage === "saved") && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg p-6 border border-teal-200">
            <h3 className="text-lg font-bold text-teal-900 mb-4 flex items-center gap-2">
              <Unlock className="w-5 h-5" />
              Decrypt Stored Data
            </h3>
            <p className="text-sm text-teal-800 mb-4">
              Step 3: Select and decrypt previously encrypted tables from CrateDB
            </p>
          </div>

          {/* Decryption Section */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-3">
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
                <Database className="w-5 h-5 text-teal-600" />
                Available Encrypted Tables
              </h3>
              <p className="text-sm text-gray-600">
                {encryptedTables.length} encrypted table(s) available for decryption
              </p>
            </div>

            <div className="p-6 mb-5">
              <div className="space-y-4">
                {/* Encrypted Tables Dropdown */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wider">
                    Select Encrypted Table
                  </label>
                  <div className="relative">
                    <div
                      className="flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-gray-300 cursor-pointer hover:border-teal-400 transition-colors"
                      onClick={() =>
                        setIsTableDropdownOpen(!isTableDropdownOpen)
                      }
                    >
                      <div className="flex items-center gap-2">
                        <Database className="w-4 h-4 text-teal-600" />
                        <span className="text-sm font-medium text-gray-700">
                          {selectedTable
                            ? selectedTable.table_name
                            : "Choose table..."}
                        </span>
                      </div>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 transition-transform ${
                          isTableDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>

                    {isTableDropdownOpen && (
                      <div className="absolute top-full mt-1 w-full bg-white rounded-lg border border-gray-200 shadow-xl z-50">
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <Search className="w-3 h-3 text-gray-400 absolute left-3 top-2.5" />
                            <input
                              type="text"
                              placeholder="Search tables..."
                              className="w-full pl-8 pr-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                              value={searchTableTerm}
                              onChange={(e) =>
                                setSearchTableTerm(e.target.value)
                              }
                            />
                          </div>
                        </div>
                        <div className="max-h-40 overflow-y-auto">
                          {encryptedTables && encryptedTables.length > 0 ? (
                            encryptedTables
                              .filter((table) =>
                                (table.table_name || "")
                                  .toLowerCase()
                                  .includes(searchTableTerm.toLowerCase())
                              )
                              .map((table) => (
                                <div
                                  key={table.full_name || table.table_name}
                                  className="px-4 py-3 hover:bg-teal-50 cursor-pointer border-b border-gray-100 transition-colors last:border-0"
                                  onClick={() => {
                                    setSelectedTable(table);
                                    setIsTableDropdownOpen(false);
                                    setSearchTableTerm("");
                                  }}
                                >
                                  <p className="text-sm font-semibold text-gray-900">
                                    {table.table_name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Schema: {table.schema || "doc"}
                                  </p>
                                </div>
                              ))
                          ) : (
                            <div className="px-4 py-6 text-center text-gray-500 text-sm">
                              No encrypted tables found. Encrypt and save data first.
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  onClick={handleDecrypt}
                  disabled={isLoading || !selectedTable}
                  className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Decrypting...
                    </>
                  ) : (
                    <>
                      <Unlock className="w-4 h-4" />
                      Step 3: Decrypt Table
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stage: Decrypted - Show decrypted preview */}
      {stage === "decrypted" && decryptedPreview && (
        <div className="space-y-6">
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-teal-900 mb-2">
                  ✓ Decryption Complete
                </h3>
                <p className="text-sm text-teal-800 mb-3">
                  Table restored: <span className="font-mono font-bold">{decryptedPreview.restored_table}</span>
                </p>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="bg-white rounded p-3 border border-teal-200">
                    <p className="text-xs text-teal-700 font-semibold mb-1">
                      Columns Decrypted
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {decryptedPreview.columns_decrypted?.length || 0}
                    </p>
                  </div>
                  <div className="bg-white rounded p-3 border border-teal-200">
                    <p className="text-xs text-teal-700 font-semibold mb-1">
                      Rows Restored
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {decryptedPreview.rows_processed || 0}
                    </p>
                  </div>
                  <div className="bg-white rounded p-3 border border-teal-200">
                    <p className="text-xs text-teal-700 font-semibold mb-1">
                      Total Columns
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {decryptedPreview.total_columns || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Decrypted Preview */}
          {decryptedPreview.sample_preview?.data && (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Decrypted Data Preview (Sample Rows)
                </h3>
              </div>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      {Object.keys(decryptedPreview.sample_preview.data[0] || {}).map(
                        (key) => (
                          <th
                            key={key}
                            className="px-4 py-2 text-left font-bold text-gray-700 whitespace-nowrap"
                          >
                            {key}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {decryptedPreview.sample_preview.data.map((row, idx) => (
                      <tr
                        key={idx}
                        className="border-b border-gray-200 hover:bg-gray-50"
                      >
                        {Object.values(row).map((value, i) => (
                          <td
                            key={i}
                            className="px-4 py-2 text-gray-700 break-words max-w-xs"
                          >
                            {String(value)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Action Button */}
          <div>
            {/* <button
              onClick={resetWorkflow}
              className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Back to Protection Dashboard
            </button> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default PIIProtectionTab;